import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { JobPostingSchema, BreadcrumbSchema } from '@/components/JsonLd';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SocialShare from '@/components/SocialShare';
import { 
  MapPin, DollarSign, Clock, Building2, Briefcase, 
  Users, ArrowLeft, Edit, Send, CheckCircle, Sparkles, Loader2, Star, User, CalendarDays,
} from 'lucide-react';

const relativeTime = (iso) => {
  if (!iso) return '';
  try {
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))}w ago`;
    return `${Math.floor(diff / (86400 * 30))}mo ago`;
  } catch { return ''; }
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [matches, setMatches] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [showMatches, setShowMatches] = useState(false);

  const isRecruiter = ['super_admin', 'client_admin', 'team_leader', 'recruiter'].includes(user?.role);
  const canEdit = isRecruiter && user?.organization_id === job?.org_id;

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`);
        setJob(response.data);
        
        // Check if user has already applied
        if (user && !isRecruiter) {
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const appsRes = await axios.get(`${API}/applications`, { headers, withCredentials: true });
          const applied = appsRes.data.applications?.some(app => app.job_id === jobId);
          setHasApplied(applied);
        }
      } catch (error) {
        console.error('Failed to fetch job:', error);
        toast.error('Job not found');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, user, token, navigate, isRecruiter]);

  const handleApply = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/jobs/${jobId}` } } });
      return;
    }

    setApplying(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/applications`, {
        job_id: jobId,
        cover_letter: coverLetter
      }, { headers, withCredentials: true });
      
      toast.success('Application submitted successfully!');
      setHasApplied(true);
      setShowApplyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/jobs/${jobId}/status?status=${status}`, {}, { headers, withCredentials: true });
      setJob(prev => ({ ...prev, status }));
      toast.success(`Job ${status === 'published' ? 'published' : status === 'closed' ? 'closed' : 'saved as draft'}`);
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const fetchMatches = async () => {
    setMatchLoading(true);
    setShowMatches(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/matching/job/${jobId}/candidates`, { headers, withCredentials: true });
      setMatches(res.data);
    } catch {
      toast.error('Failed to fetch matches');
    }
    setMatchLoading(false);
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    const format = (n) => `$${(n/1000).toFixed(0)}k`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    if (min) return `From ${format(min)}`;
    return `Up to ${format(max)}`;
  };

  const getJobTypeBadge = (type) => {
    const config = {
      full_time: { label: 'Full Time', className: 'bg-green-100 text-green-800' },
      part_time: { label: 'Part Time', className: 'bg-blue-100 text-blue-800' },
      contract: { label: 'Contract', className: 'bg-orange-100 text-orange-800' },
      internship: { label: 'Internship', className: 'bg-purple-100 text-purple-800' },
    };
    const c = config[type] || config.full_time;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const content = (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="job-detail-page">
      <SEO
        title={job ? `${job.title} - ${job.company_name || 'Teaching Job'}` : 'Job Detail'}
        description={job ? `${job.title} at ${job.company_name || 'an institution'}. ${job.location ? `Location: ${job.location}.` : ''} ${job.job_type ? `Type: ${job.job_type}.` : ''} Apply now on EdAssist.` : ''}
        path={`/jobs/${jobId}`}
        type="article"
      />
      {job && <JobPostingSchema job={job} />}
      {job && <BreadcrumbSchema items={[{ name: 'Jobs', url: '/jobs' }, { name: job.title }]} />}
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Job Header */}
      <Card>
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex gap-4">
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                {job.organization?.logo ? (
                  <img src={job.organization.logo} alt="" className="w-10 h-10 rounded" />
                ) : (
                  <Building2 className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {getJobTypeBadge(job.job_type)}
                  {job.is_featured && (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-testid="detail-featured-badge">
                      <Star className="w-3 h-3 mr-1 fill-current" /> Featured
                    </Badge>
                  )}
                  {isRecruiter && (
                    <Badge variant={job.status === 'published' ? 'default' : 'secondary'}>
                      {job.status}
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  {job.title}
                </h1>
                <p className="text-lg text-slate-600 mt-1">{job.organization?.name || 'Company'}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {canEdit && (
                <>
                  <Link to={`/jobs/${jobId}/edit`}>
                    <Button variant="outline" data-testid="edit-job-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </Link>
                  {job.status === 'draft' && (
                    <Button onClick={() => handleStatusChange('published')} data-testid="publish-job-btn">
                      Publish Job
                    </Button>
                  )}
                  {job.status === 'published' && (
                    <Button variant="secondary" onClick={() => handleStatusChange('closed')} data-testid="close-job-btn">
                      Close Job
                    </Button>
                  )}
                </>
              )}
              {!isRecruiter && job.status === 'published' && (
                hasApplied ? (
                  <Button disabled className="bg-green-600" data-testid="applied-btn">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Applied
                  </Button>
                ) : (
                  <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="apply-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Apply Now
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit' }}>Apply for {job.title}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Cover Letter (Optional)</label>
                          <Textarea
                            placeholder="Tell us why you'd be a great fit for this role..."
                            value={coverLetter}
                            onChange={(e) => setCoverLetter(e.target.value)}
                            className="mt-2 min-h-[150px]"
                            data-testid="cover-letter-input"
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleApply} disabled={applying} data-testid="submit-application-btn">
                            {applying ? 'Submitting...' : 'Submit Application'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )
              )}
            </div>
          </div>

          {/* Job Meta */}
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-5 h-5" />
              <span>{job.location}</span>
            </div>
            {formatSalary(job.salary_min, job.salary_max) && (
              <div className="flex items-center gap-2 text-slate-600">
                <DollarSign className="w-5 h-5" />
                <span>{formatSalary(job.salary_min, job.salary_max)}</span>
              </div>
            )}
            {job.experience_years && (
              <div className="flex items-center gap-2 text-slate-600">
                <Briefcase className="w-5 h-5" />
                <span>{job.experience_years}+ years experience</span>
              </div>
            )}
            {job.department && (
              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-5 h-5" />
                <span>{job.department}</span>
              </div>
            )}
            {job.posted_at && (
              <div className="flex items-center gap-2 text-slate-600" title={formatDate(job.posted_at)}>
                <CalendarDays className="w-5 h-5" />
                <span>Posted on {formatDate(job.posted_at)} <span className="text-slate-400">· {relativeTime(job.posted_at)}</span></span>
              </div>
            )}
          </div>

          {/* Social Share */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <SocialShare
              title={`${job.title} at ${job.organization?.name || job.company_name || 'an institution'}`}
              text={`Job opening: ${job.title}${job.location ? ` in ${job.location}` : ''} — apply on EdAssist`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Job Description */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Job Description</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-slate max-w-none">
            {job.description.split('\n').map((para, idx) => (
              <p key={idx} className="text-slate-700 leading-relaxed mb-4">
                {para}
              </p>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      {job.skills?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Required Skills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((skill, idx) => (
                <Badge key={idx} variant="outline" className="px-3 py-1">
                  {skill}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Match Panel for Recruiters */}
      {isRecruiter && (
        <Card data-testid="smart-match-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Sparkles className="w-5 h-5 text-indigo-600" />
                Smart Match — Best Candidates
              </CardTitle>
              <Button onClick={fetchMatches} disabled={matchLoading} size="sm" data-testid="find-matches-btn">
                {matchLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {matches ? 'Refresh Matches' : 'Find Best Matches'}
              </Button>
            </div>
          </CardHeader>
          {showMatches && (
            <CardContent>
              {matchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mr-2" />
                  <span className="text-slate-500">Analyzing candidate profiles...</span>
                </div>
              ) : matches?.matches?.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">{matches.total_candidates} candidates scored — showing top matches</p>
                  {matches.matches.map((m, idx) => (
                    <div key={m.user_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`match-card-${idx}`}>
                      <div className="flex-shrink-0 w-8 text-center">
                        <span className="text-xs font-bold text-slate-400">#{idx + 1}</span>
                      </div>
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{m.name || 'Candidate'}</p>
                        <p className="text-xs text-slate-500 truncate">{m.email}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {m.skills?.slice(0, 4).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs px-1.5 py-0">{s}</Badge>
                          ))}
                          {m.experience_years && <Badge className="text-xs px-1.5 py-0 bg-slate-100 text-slate-600">{m.experience_years} yrs exp</Badge>}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                          m.match_score >= 70 ? 'border-green-500 bg-green-50' :
                          m.match_score >= 40 ? 'border-yellow-500 bg-yellow-50' :
                          'border-slate-300 bg-slate-50'
                        }`}>
                          <span className={`text-sm font-bold ${
                            m.match_score >= 70 ? 'text-green-700' :
                            m.match_score >= 40 ? 'text-yellow-700' :
                            'text-slate-600'
                          }`}>{m.match_score}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <User className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No matching candidates found. Candidates need to complete their profiles.</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Bottom CTA */}
      {!isRecruiter && job.status === 'published' && !hasApplied && (
        <Card className="bg-indigo-50 border-indigo-100">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Interested in this role?
            </h3>
            <p className="text-slate-600 mb-4">Apply now and take the next step in your career.</p>
            <Button onClick={() => user ? setShowApplyDialog(true) : navigate('/login')} data-testid="bottom-apply-btn">
              <Send className="w-4 h-4 mr-2" />
              Apply Now
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  if (!user) {
    return <PublicLayout>{content}</PublicLayout>;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default JobDetailPage;
